/**
 * InventoryTransferPage — házon-belüli raktárközi átcsoportosítás (multi-warehouse Phase-1b).
 *
 * A kész BE warehouse.controller (POST /warehouses/transfers) bedrótozása:
 * forrás-raktár → cél-raktár, a forrás elérhető tételeiből kiválasztott
 * mennyiségek. Számla nélkül (1 cég, házon belüli). Készlet-write — a transfer
 * PENDING státuszban jön létre (atomi forrás-dekrement + cél-inkrement a BE-n).
 */

import type { InventoryItem } from '@/api/inventory';
import { Button, Card, CardContent } from '@/components/ui';
import { useCreateWarehouseTransfer, useInventory, useWarehouses } from '@/hooks/use-inventory';
import { useTranslation } from '@kgc/i18n';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface TransferLine {
  inventoryItemId: string;
  productName: string;
  available: number;
  unit: string;
  quantity: number;
}

export function InventoryTransferPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { warehouses } = useWarehouses();
  const { transfer, isLoading: isSubmitting, error: submitError } = useCreateWarehouseTransfer();

  const [sourceWarehouseId, setSourceWarehouseId] = useState('');
  const [targetWarehouseId, setTargetWarehouseId] = useState('');
  const [reason, setReason] = useState('');
  const [lines, setLines] = useState<TransferLine[]>([]);
  const [createdId, setCreatedId] = useState<string | null>(null);

  // A forrás-raktár elérhető (AVAILABLE) tételei — csak ha van kiválasztott forrás.
  const { items } = useInventory(
    sourceWarehouseId ? { warehouseId: sourceWarehouseId, status: 'AVAILABLE' } : {}
  );
  const sourceItems = sourceWarehouseId ? items : [];

  const targetOptions = useMemo(
    () => warehouses.filter(wh => wh.id !== sourceWarehouseId),
    [warehouses, sourceWarehouseId]
  );

  const addLine = (item: InventoryItem) => {
    if (lines.some(l => l.inventoryItemId === item.id)) return;
    setLines(prev => [
      ...prev,
      {
        inventoryItemId: item.id,
        productName: item.productName ?? item.productSku ?? item.id,
        available: item.quantity,
        unit: item.unit,
        quantity: 1,
      },
    ]);
  };

  const updateQty = (id: string, qty: number) => {
    setLines(prev => prev.map(l => (l.inventoryItemId === id ? { ...l, quantity: qty } : l)));
  };

  const removeLine = (id: string) => {
    setLines(prev => prev.filter(l => l.inventoryItemId !== id));
  };

  const sameWarehouse =
    !!sourceWarehouseId && !!targetWarehouseId && sourceWarehouseId === targetWarehouseId;

  const canSubmit =
    !!sourceWarehouseId &&
    !!targetWarehouseId &&
    !sameWarehouse &&
    lines.length > 0 &&
    lines.every(l => l.quantity > 0 && l.quantity <= l.available);

  const handleSubmit = async () => {
    setCreatedId(null);
    try {
      const created = await transfer({
        sourceWarehouseId,
        targetWarehouseId,
        reason: reason || undefined,
        items: lines.map(l => ({
          inventoryItemId: l.inventoryItemId,
          quantity: l.quantity,
          unit: l.unit,
        })),
      });
      setCreatedId(created.id);
      setLines([]);
      setReason('');
    } catch {
      // A hibát a hook submitError-ja jelzi.
    }
  };

  return (
    <div className="min-h-screen kgc-bg">
      <header className="bg-white dark:bg-[#1a4f5c] shadow-sm">
        <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-4 sm:px-6 lg:px-8">
          <Button variant="ghost" onClick={() => navigate('/inventory')}>
            {t('common.back')}
          </Button>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              {t('inventory.transferTitle')}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('inventory.transferSubtitle')}
            </p>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {createdId && (
          <Card className="mb-6 border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20">
            <CardContent className="py-4">
              <p className="text-green-700 dark:text-green-400">
                {t('inventory.transferSuccess', { id: createdId })}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Forrás + cél raktár */}
        <div className="mb-6 grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('inventory.transferSource')}
            </label>
            <select
              value={sourceWarehouseId}
              onChange={e => {
                setSourceWarehouseId(e.target.value);
                setLines([]);
              }}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            >
              <option value="">{t('inventory.transferSelectSource')}</option>
              {warehouses.map(wh => (
                <option key={wh.id} value={wh.id}>
                  {wh.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('inventory.transferTarget')}
            </label>
            <select
              value={targetWarehouseId}
              onChange={e => setTargetWarehouseId(e.target.value)}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            >
              <option value="">{t('inventory.transferSelectTarget')}</option>
              {targetOptions.map(wh => (
                <option key={wh.id} value={wh.id}>
                  {wh.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {sameWarehouse && (
          <p className="mb-4 text-sm text-red-600 dark:text-red-400">
            {t('inventory.transferSameWarehouseError')}
          </p>
        )}

        {/* Indok */}
        <div className="mb-6">
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('inventory.transferReason')}
          </label>
          <input
            type="text"
            value={reason}
            maxLength={500}
            onChange={e => setReason(e.target.value)}
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
          />
        </div>

        {/* Elérhető tételek a forrás raktárból */}
        {sourceWarehouseId && (
          <Card className="mb-6">
            <CardContent className="p-4">
              <h2 className="mb-3 font-semibold text-gray-900 dark:text-white">
                {t('inventory.transferAvailableItems')}
              </h2>
              {sourceItems.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('inventory.transferNoItems')}
                </p>
              ) : (
                <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                  {sourceItems.map(item => (
                    <li key={item.id} className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-900 dark:text-white">
                        {item.productName ?? item.productSku ?? item.id}
                        <span className="ml-2 text-gray-500 dark:text-gray-400">
                          ({item.quantity} {item.unit})
                        </span>
                      </span>
                      <Button
                        variant="outline"
                        onClick={() => addLine(item)}
                        disabled={lines.some(l => l.inventoryItemId === item.id)}
                      >
                        {t('inventory.transferAdd')}
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        )}

        {/* Átcsoportosítandó tételek */}
        {lines.length > 0 && (
          <Card className="mb-6">
            <CardContent className="p-4">
              <h2 className="mb-3 font-semibold text-gray-900 dark:text-white">
                {t('inventory.transferAddedItems')}
              </h2>
              <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                {lines.map(line => (
                  <li key={line.inventoryItemId} className="flex items-center gap-3 py-2">
                    <span className="flex-1 text-sm text-gray-900 dark:text-white">
                      {line.productName}
                    </span>
                    <input
                      type="number"
                      min={1}
                      max={line.available}
                      value={line.quantity}
                      onChange={e => updateQty(line.inventoryItemId, Number(e.target.value))}
                      aria-label={t('inventory.transferQuantity')}
                      className="w-20 rounded-md border border-gray-300 bg-white px-2 py-1 text-right text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                    />
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {t('inventory.transferAvailable', { qty: line.available })}
                    </span>
                    <Button variant="ghost" onClick={() => removeLine(line.inventoryItemId)}>
                      {t('inventory.transferRemove')}
                    </Button>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {submitError && (
          <p className="mb-4 text-sm text-red-600 dark:text-red-400">{submitError}</p>
        )}

        <Button onClick={() => void handleSubmit()} disabled={!canSubmit || isSubmitting}>
          {isSubmitting ? t('common.processing') : t('inventory.transferSubmit')}
        </Button>
      </main>
    </div>
  );
}
